import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators, FormBuilder } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { faCheck, faPrint, faWindowClose } from '@fortawesome/free-solid-svg-icons';
import { ExtendedProfile } from 'src/app/common/ExtendedProfile';
import { Order } from 'src/app/common/Order';
import { AppProfileService } from 'src/app/services/profile.service';
import { OrderService } from 'src/app/services/order.service';
import { CommonModule, Location } from '@angular/common';
import { ClaimOrderModel } from 'src/app/common/ClaimOrderModel';
import { ClaimOrderPositionModel } from 'src/app/common/ClaimOrderPositionModel';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { OrderPositionStatusDisplayNamePipe } from '../../pipes/order-position-status.pipe';
import { PDFExportModule } from '@progress/kendo-angular-pdf-export';
import { OrderPositionStatusConstants } from '../../common/OrderPositionStatus';
import { CallApiService } from '../../services/api-action.service';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { EMPTY, Observable, tap } from 'rxjs';

/** The seller's order form; its type is inferred from here (#199). */
function buildOrderForm(fb: FormBuilder, order: Order) {
  return fb.group({
    orderNumber: [order?.orderNumber, Validators.required],
    buyerCustomerNumber: [order?.buyerCustomerNumber],
    buyerCustomerFullName: [order?.buyerCustomerFullName],
    orderDateTime: [order?.orderDateTime],
    orderIsInvoiced: [Order.isInvoiced(order) ? 'Ja' : 'Nein'],
    invoiceNumber: [Order.getInvoice(order)?.invoiceNumber],
    id: [order?.id],
    isReadyForInvoice: [order?.isReadyForInvoice],
  });
}
type OrderForm = ReturnType<typeof buildOrderForm>;

@Component({
    imports: [CommonModule, ReactiveFormsModule, FontAwesomeModule, OrderPositionStatusDisplayNamePipe, PDFExportModule, AsyncClickDirective, RouterLink],
    selector: 'app-order-edit',
    templateUrl: './order-seller-edit.component.html'
})
export class OrderSellerEditComponent implements OnInit {
  fb = inject(FormBuilder);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private location = inject(Location);
  private profileService = inject(AppProfileService);
  private orderService = inject(OrderService);
  private callApiService = inject(CallApiService);

  private destroyRef = inject(DestroyRef);
  submitted: boolean;
  constructor() {
    this.order = new Order();
    this.orderForm = this.createForm(this.order);
  }

  OrderPositionStatusConstants = OrderPositionStatusConstants;
  public showRejectCancellationButton = false;
  public showApproveCancellationButton = false;
  public order: Order;
  public faCheck = faCheck;
  public faWindowClose = faWindowClose;
  public faPrint = faPrint;
  public orderForm: OrderForm;
  public isApprovalMode: boolean = false;
  public profile: ExtendedProfile = null;

  private id: string;

  ngOnInit() {
    this.id = this.activatedRoute.snapshot.paramMap.get('id');

    this.profileService.currentProfile$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((p) => {
      this.profile = p;
    });

    this.refresh();
  }

  reloadOrder() {
    if (this.id) {
      this.orderService.getById(this.id).subscribe((c) => {
        this.order = c;
        this.orderForm = this.createForm(this.order);
      });
    }
  }

  createForm(order: Order): OrderForm {
    return buildOrderForm(this.fb, order);
  }

  onBackClicked() {
    this.location.back();
  }

  // Returns a factory so [asyncClick] guards the per-position approve against a
  // double-click; refresh() now runs only after the request succeeds (the previous
  // eager refresh() before the response was a no-op bug).
  onApproveOrderPosition(orderPositionId: string): () => Observable<unknown> {
    return () => {
      const orderClaimModel = new ClaimOrderModel(this.order.orderGroupId);
      orderClaimModel.claimOrderPositions.push(new ClaimOrderPositionModel(orderPositionId));

      return this.callApiService
        .callWithToasts$(
          this.orderService.approveOrderPositionClaims(orderClaimModel),
          $localize`Stornierung wurde genehmigt`,
          $localize`Bei der Genehmigung der Stornierung ist ein Fehler aufgetreten`
        )
        .pipe(tap(() => this.refresh()));
    };
  }

  onRejectOrderPosition(orderPositionId: string): () => Observable<unknown> {
    return () => {
      const orderClaimModel = new ClaimOrderModel(this.order.orderGroupId);
      orderClaimModel.claimOrderPositions.push(new ClaimOrderPositionModel(orderPositionId));

      return this.callApiService
        .callWithToasts$(
          this.orderService.rejectOrderPositionClaims(orderClaimModel),
          $localize`Stornierung wurde abgelehnt`,
          $localize`Bei der Ablehnung der Stornierung ist ein Fehler aufgetreten`
        )
        .pipe(tap(() => this.refresh()));
    };
  }

  onSetInvoiceReadyStatus(): Observable<unknown> {
    if (!this.orderForm.valid || !this.order.id) {
      return EMPTY;
    }
    this.order.isReadyForInvoice = this.orderForm.controls.isReadyForInvoice.value;
    this.submitted = true;

    return this.callApiService
      .callWithToasts$(
        this.orderService.updateForInvoiceReady(this.order.id, this.order),
        $localize`Rechnungsfreigabe wird aktualisiert.`,
        $localize`Beim Aktualisieren der Rechnungsfreigabe ist ein Fehler aufgetreten.`
      )
      .pipe(tap(() => this.router.navigateByUrl('shop-order-history')));
  }

  private refresh() {
    this.id = this.activatedRoute.snapshot.paramMap.get('id');
    this.showRejectCancellationButton = Boolean(
      JSON.parse(this.activatedRoute.snapshot.queryParamMap.get('showRejectCancellationButton'))
    );
    this.showApproveCancellationButton = Boolean(
      JSON.parse(this.activatedRoute.snapshot.queryParamMap.get('showApproveCancellationButton'))
    );
    this.isApprovalMode = this.showRejectCancellationButton || this.showApproveCancellationButton;

    this.reloadOrder();
  }

  public onIsReadyForInvoiceChanged(_e: Event) {
    this.refresh();
  }

  get f() {
    return this.orderForm.controls;
  }
}
